import { Box, Button, Card, CardContent, Checkbox, CircularProgress, Dialog, DialogTitle, IconButton, List, ListItem, ListItemAvatar, ListItemText, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography } from '@mui/material'
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next';
import { api } from 'src/api/endpoints';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatWebsiteUrl } from './DialogBoxWebSite';
import { useSelector } from 'react-redux';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';


const DialogPMWcreator = ({isOpen, onClose, onAddWidget, isIcon}) => {
    const { t } = useTranslation();

    const { currentFolder } = useSelector((state) => state.FileFolder);
    const { user } = useSelector((state) => state.User);

    const [pmwUserId, setPmwUserId] = useState('')
    const [pmwToken, setPmwToken] = useState('')

    const [designsSearchKey, setDesignsSearchKey] = useState('')

    const [mode, setMode] = useState(1)
    const [error, setError] = useState('')
    const [designs, setDesigns] = useState([])
    const [selectedDesign, setSelectedDesign] = useState('')
    const [pmwUserData, setPmwUserData] = useState(null)
    const [showLogoutInfo, setShowLogoutInfo] = useState(false)
    const isSavingRef = useRef(false)

    const getStoredCreds = useCallback(() => {
        try {
            return JSON.parse(window.localStorage.getItem('PMWcred') || '{}')
        } catch (e) {
            return {}
        }
    }, [])

    const clearPmwSession = useCallback(() => {
        window.localStorage.removeItem('PMWcred')
        setPmwToken('')
        setPmwUserId('')
        setDesignsSearchKey('')
        setDesigns([])
        setSelectedDesign('')
        setPmwUserData(null)
        setError('')
        setMode(1)
    }, [])

    const createPMWProfileUrl = useCallback(() => {
        return process.env.REACT_APP_PMW_PROFILE_URL || 'https://www.postermywall.com/index.php/authenticate/showlogin/?redirect=https%3A%2F%2Fwww.postermywall.com%2Findex.php'
    }, [])

    const handleLogout = useCallback(() => {
        clearPmwSession()
        setShowLogoutInfo(true)
    }, [clearPmwSession])

    const handleLogoutAndReauthorize = useCallback(() => {
        clearPmwSession()
        window.location.href = createPMWurl()
    }, [clearPmwSession])

    useEffect(() => {
        const creds = getStoredCreds()
        if (creds?.expires_at > Date.now() && creds?.access_token && creds?.user_id) {
            setPmwToken(creds?.access_token)
            setPmwUserId(creds?.user_id)
            getDesigns(creds?.user_id, creds?.access_token)
            getUserData(creds?.user_id, creds?.access_token)
            setMode(2)          
        }else {
            setPmwToken('')
            setPmwUserId('')
            setPmwUserData(null)
            setMode(1)
        }     
    }, [isOpen])

    useEffect(() => {
        if (mode == 3) {
            openPMWeditor()               
        }
    }, [mode])

    const closeAndReset = () => {
        onClose(false)
        setError('')
        setShowLogoutInfo(false)
        setMode(1)        
    }

    const getUserData = async (uid=pmwUserId, ptk=pmwToken) => {
        try {
            const {data, status} = await api.get(`/postermywall/users/${uid}`, {
                headers: {
                    "Authorization": `Bearer ${ptk}`
                }
            })
            // console.log('designs', data)
            setPmwUserData(data)
            return data
        } catch (error) {
            setError(error.message)
        }
    }

    const getDesigns = async (uid=pmwUserId, ptk=pmwToken) => {
        try {
            const {data, status} = await api.get(`/postermywall/users/${uid}/designs`, {
                headers: {
                    "Authorization": `Bearer ${ptk}`
                }
            })
            // console.log('designs', data)
            setDesigns(data)
            return data
        } catch (error) {
            setError(error.message)
        }
    }

    const designsFiltered = useMemo(() => {
        const searchKey = designsSearchKey.trim().toLowerCase()

        if (!searchKey) {
            return designs
        }

        return designs.filter((design) => {
            const designName = design?.name?.toLowerCase() || ''
            return designName.includes(searchKey)
        })
    }, [designs, designsSearchKey])

    const scriptContainerRef = useRef(null)

    const openPMWeditor = () => {
        // provide callback before injecting script to avoid race
        window.pmwPluginEditorInit = () => {
            window.PMW.plugin.editor.open({
                mode: 'normal',
                dimensions: {width: 1080, height: 1080, units: 'px'},
                contentType: 'all',
                elementsToHide: ['upgrade'],
                onClose: (id) => {
                    alert("Ensure that you saved your design")
                    console.log(id)
                    // closeAndReset()
                    setMode(2)
                },
                onSave: async (id) => {
                    console.log(id)
                    window.PMW.plugin.editor.close()
                    const dsgns = await getDesigns()
                    await getDesignDownaloadURL(id, dsgns)
                    // setMode(2)
                }
            })
            console.log("PMW loaded")
        };

        // use player id so initPMw picks embed/player script
        initPMw(scriptContainerRef, 'script', 'pmw-plugin-player')
    }

    const createPMWurl = () => {
        const pmwClientID = process.env.REACT_APP_PMW_CLIENT_ID
        const pmwRedirectUrl = process.env.REACT_APP_PMW_REDIRECT_URL
        const pmwBaseURL = new URL('https://api.postermywall.com/v1/oauth/authorize')
        pmwBaseURL.searchParams.set('client_id', pmwClientID)
        pmwBaseURL.searchParams.set('redirect_uri', pmwRedirectUrl)
        pmwBaseURL.searchParams.set('response_type', 'code')
        return pmwBaseURL.toString()
    }

    const getDesignDownaloadURL = async (did, dgns=designs, uid=pmwUserId, ptk=pmwToken) => {
        if (isSavingRef.current) return
        isSavingRef.current = true
        try {
            setMode(4)
            const {data, status} = await api.get(`/postermywall/users/${uid}/designs/${did}/download`, {
                headers: {
                    "Authorization": `Bearer ${ptk}`
                }            
            })
            if (status == 200) {
                const selectedDesignData = dgns.find(d => d.id == did)
                await onAddWidget({ name: selectedDesignData.name, type: 'widget', widgetCat: 'postermywall', widgetIcon: isIcon, intervalValue: '', url: formatWebsiteUrl(data.download_url), folderId: currentFolder, user: user._id })
                closeAndReset()
            }
        } catch (error) {
            console.log(error)
            setError(error.message)
        } finally {
            isSavingRef.current = false
        }
    }

    const initPMw = (containerOrDoc, s, id) => {
        const editorSrc = 'https://d1csarkz8obe9u.cloudfront.net/plugins/editor/postermywall-editor-v5.js';
        // const editorSrc = 'https://d1csarkz8obe9u.cloudfront.net/plugins/editor/postermywall-editor-v5.js';
        // const editorSrc = 'https://d1csarkz8obe9u.cloudfront.net/plugins/editor/postermywall-editor-v2.js'

        // Resolve container element: could be a ref object or document
        let container = containerOrDoc && containerOrDoc.current ? containerOrDoc.current : containerOrDoc;
        if (!container) container = document;

        // If a script with this id already exists inside the container or document, don't inject again
        try {
            if (container.querySelector && container.querySelector(`#${id}`)) return;
        } catch (e) {}

        const js = document.createElement(s);
        js.id = id;
        js.async = true;
        js.src = editorSrc.replace('xhr.send(options.hasContent && options.data || null)', '');

        js.onload = () => {
            if (typeof window.pmwPluginEditorInit === 'function') {
                try { window.pmwPluginEditorInit(); } catch (e) { console.warn('pmw init callback error', e); }
            }
            window.postermywallScriptLoaded = true;
        };

        js.onerror = (e) => {
            console.error('Failed to load PosterMyWall script', e);
        };

        // If container is the document node, insert before first script; otherwise append to the given container element
        if (container === document) {
            const firstScript = document.getElementsByTagName(s)[0];
            if (firstScript && firstScript.parentNode) firstScript.parentNode.insertBefore(js, firstScript);
            else document.head.appendChild(js);
        } else if (container.appendChild) {
            container.appendChild(js);
        } else {
            const firstScript = document.getElementsByTagName(s)[0];
            if (firstScript && firstScript.parentNode) firstScript.parentNode.insertBefore(js, firstScript);
            else document.head.appendChild(js);
        }
    }

  return (
    <Dialog fullWidth open={isOpen} onClose={() => closeAndReset()} maxWidth={"md"}>
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: "center",
            gap: 1,
            px: 3,
            pt: 2,
            pb: 0
        }}>
        {pmwUserData && (
            <List sx={{
                // p: 2
            }}>
                <ListItem>
                    <ListItemAvatar>
                        <AccountCircleIcon />
                    </ListItemAvatar>
                    <ListItemText primary={pmwUserData?.display_name} />
                </ListItem>
            </List>
        )}
        {(pmwUserId || pmwToken || window.localStorage.getItem('PMWcred')) && (
                <Button size='small' variant='text' color='inherit' startIcon={<LogoutIcon />} onClick={handleLogout}>Logout</Button>
        )}        
        </Box>
        <Box sx={{
            px: 3,
            pb: 3
        }}>
            {error ? (
                <>
                    <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
                    <img src={"./images/postermywallLogo.png"} alt="Logo" width={"150px"} />
                    </Box>
                    <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>{t("views.dialogBoxPostermywall.title")}</DialogTitle>

                    <Typography variant={"subtitle1"} sx={{
                        mt: 2
                    }} textAlign={"center"}>Error occured</Typography>

                    <Box sx={{
                        display: "flex",
                        justifyContent: "center"
                    }}>
                        {window.localStorage.getItem("PMWcred") ? (
                            <Button variant='contained' onClick={() => {
                                setMode(1)
                                setError('')
                            }}>Try again</Button>
                        ) : (
                            <a href={createPMWurl()}><Button variant='contained'>Try again</Button></a>
                        )}
                    </Box>                
                </>
            ) : (
                <>
                    {mode == 1 && (
                        <>
                            <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
                            <img src={"./images/postermywallLogo.png"} alt="Logo" width={"150px"} />
                            </Box>
                            <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>{t("views.dialogBoxPostermywall.title")}</DialogTitle>

                            <Typography sx={{
                                mt: 2
                            }}>Here you can create a postermywall widget that will be ready to be displayed on your screen</Typography>
                            <Typography>How does it look like step by step?</Typography>
                            <ol>
                                <li><Typography>Login to your www.postermywall.com account by clicking the button below.</Typography></li>
                                <li><Typography>You will then be redirected back to our website and you can create your own project or choose one of the already created ones.</Typography></li>
                                <li><Typography>Your PosterMyWall widget will be ready to use and listed in files tab.</Typography></li>
                            </ol>

                            <Box sx={{
                                display: "flex",
                                justifyContent: "center"
                            }}>
                                <a href={createPMWurl()}><Button variant='contained'>Login to PosterMyWall.com</Button></a>
                            </Box>
                        </>            
                    )}
                    {mode == 2 && (
                        <Box sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: '20px'
                        }}>
                            <Card sx={{
                                maxHeight: "500px",
                                width: "48%",
                                py: 2
                            }} variant='outlined'>
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: '20px',
                                    justifyContent: "center"
                                }}>
                                    <Typography variant="subtitle1" textAlign={"center"}>Choose existing design</Typography>
                                    <IconButton onClick={() => getDesigns()}>
                                        <RefreshIcon color='primary' />
                                    </IconButton>
                                    <Button variant='contained' disabled={selectedDesign ? false : true} onClick={() => getDesignDownaloadURL(selectedDesign)}>Select</Button>
                                </Box>
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: '20px',
                                    justifyContent: "center",
                                    px: 4                                  
                                }}>
                                    <TextField fullWidth size="small" variant="standard" label="Search for created design" placeholder='My design 1' value={designsSearchKey} onInput={(e) => setDesignsSearchKey(e.target.value)} />
                                </Box>
                                <CardContent sx={{
                                    overflowY: 'scroll',
                                    height: "calc(100% - 70px)",
                                }}>
                                    {designs.length > 0 ? (
                                        <TableContainer>
                                            <Table size="small" aria-label="designs table">
                                                <TableBody>
                                                    {designsFiltered.map((d) => {
                                                        const isChecked = selectedDesign == d.id
                                                        return (
                                                            <TableRow
                                                                key={d.id}
                                                                hover
                                                                onClick={() => setSelectedDesign(isChecked ? '' : d.id)}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    '&.MuiTableRow-hover:hover': {
                                                                        backgroundColor: 'action.hover'
                                                                    },
                                                                    backgroundColor: isChecked ? 'action.selected' : 'inherit'
                                                                }}
                                                            >
                                                                <TableCell width={56} padding="checkbox">
                                                                    <Checkbox
                                                                        edge="start"
                                                                        onChange={(e) => setSelectedDesign(e.target.checked ? d.id : '')}
                                                                        checked={isChecked}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        <a href={d.copy_url} target="_blank" rel="noreferrer">{d.name}</a>
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {`${d.type} ${new Date(d.created_on * 1000).toLocaleDateString()}`}
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <>
                                            <Typography variant='subtitle2' textAlign={"center"}>No designs created yet</Typography>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                            <Box sx={{
                                display: "grid",
                                gridTemplateRows: "6fr 1fr",
                                width: "48%",
                                gap: "20px",
                            }}>
                                <Card sx={{
                                    maxHeight: "400px",
                                    width: "100%",
                                    heigth: "100%",
                                    p: 2
                                }} variant='outlined'>
                                    <Typography variant="subtitle1" textAlign={"center"}>Create new one</Typography>
                                    <CardContent sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}>
                                        <a href="https://www.postermywall.com/index.php/posters/mine#/designs" target="_blank"><Button variant='contained' 
                                        // onClick={() => setMode(3)}
                                        >Click to open editor</Button></a>
                                    </CardContent>
                                </Card>   
                                <Typography variant={"caption"} textAlign={"center"} color={"text.disabled"}>
                                    If you edit your design in PosterMyWall, don't forget to re-sync it. The re-sync button is available on the main <a role='button' onClick={closeAndReset} style={{
                                        textDecoration: "underline",
                                        cursor: "pointer"
                                    }}>file list</a>
                                </Typography>                         
                            </Box>
                        </Box>
                    )}
                    {mode == 3 && (
                        <>
                            <div id='pmw-plugin-editor-container' ref={scriptContainerRef} />
                        </>
                    )}
                    {mode == 4 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: "center",
                            gap: '20px'
                        }}>
                            <Typography variant="subtitle1" textAlign={"center"}>Loading...</Typography>
                            <CircularProgress size={"30px"} />
                        </Box>
                    )}
                </>
            )}
        </Box>
        <Dialog open={showLogoutInfo} onClose={() => setShowLogoutInfo(false)} maxWidth="sm" fullWidth>
            <Box sx={{ p: 3 }}>
                <DialogTitle sx={{ px: 0, pt: 0 }}>PMW session cleared</DialogTitle>
                <Typography sx={{ mb: 2 }}>
                    Your CMS session was cleared locally. To fully switch accounts, log out from your PosterMyWall account first, then come back here and authorize again.
                </Typography>
                <Typography sx={{ mb: 2 }}>
                    Open your PosterMyWall profile or login page here:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Button variant="contained" href={createPMWProfileUrl()} target="_blank" rel="noreferrer">
                        Open PMW profile
                    </Button>
                    <Button variant="outlined" href={createPMWurl()} target="_blank" rel="noreferrer">
                        Authorize again in CMS
                    </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    After logging out in PosterMyWall, return to the CMS and authorize again to connect the new account.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button onClick={() => setShowLogoutInfo(false)}>Close</Button>
                </Box>
            </Box>
        </Dialog>
    </Dialog>
  )
}

export default DialogPMWcreator
